import { z } from 'zod';

export const RED_LINE_AVAILABILITY_STATES = [
  'CLOSED',
  'OPEN',
  'OCCUPIED',
  'DISCONNECTED',
  'EXPIRED',
] as const;

export const RED_LINE_SESSION_STATES = [
  'OPENING',
  'ACTIVE',
  'IDLE',
  'RECONNECTING',
  'ENDED',
  'CONVERTED',
] as const;

export const RED_LINE_PUBLIC_STATES = [
  'LOCKED_COUNTDOWN',
  'AVAILABLE',
  'CLOSED',
  'OCCUPIED',
  'ADMIN_DISCONNECTED',
  'COOLDOWN',
  'OPENING',
  'RECONNECTING',
  'ERROR',
] as const;

export const RED_LINE_SIGNAL_REACTIONS = [
  'PYAH',
  'WAIT',
  'LMAO',
  'REAL',
  'LOUDER',
  'HUH',
  'WARNING',
  'EYE',
] as const;

export const RED_LINE_MESSAGE_EMPHASIS = ['NORMAL', 'WHISPER', 'SHOUT', 'DEADPAN'] as const;

export const RED_LINE_CONTEXT_CHIPS = [
  'Just saying hi',
  'Quick question',
  'Need feedback',
  'Found something',
  'Chaos',
  'Other',
] as const;

export const RED_LINE_OPEN_DURATIONS = [15 * 60, 30 * 60, 60 * 60, 0] as const;

export const RED_LINE_CLOSING_PRESETS = [
  'Good transmission. Closing the line.',
  'I need to step away. This thread will remain available.',
  'The Red Line is closing, but this can continue as a normal conversation.',
  'This channel is being closed due to misuse.',
] as const;

export const RED_LINE_CONFIG = {
  maxConcurrentSessions: 1,
  defaultOpenDurationSeconds: 30 * 60,
  idleWarningSeconds: 4 * 60,
  idleTimeoutSeconds: 5 * 60,
  maxSessionDurationSeconds: 30 * 60,
  visitorReopenCooldownSeconds: 10 * 60,
  adminHeartbeatGraceSeconds: 45,
  visitorReconnectGraceSeconds: 90,
  maxPriorityPings: 1,
  voiceBurstLimitSeconds: 20,
  signalRateLimitPerMinute: 30,
  messageRateLimitPerMinute: 20,
  reservationExpirySeconds: 30,
} as const;

export type RedLineAvailabilityState = (typeof RED_LINE_AVAILABILITY_STATES)[number];
export type RedLineSessionState = (typeof RED_LINE_SESSION_STATES)[number];
export type RedLinePublicState = (typeof RED_LINE_PUBLIC_STATES)[number];
export type RedLineSignalReaction = (typeof RED_LINE_SIGNAL_REACTIONS)[number];
export type RedLineMessageEmphasis = (typeof RED_LINE_MESSAGE_EMPHASIS)[number];
export type RedLineContextChip = (typeof RED_LINE_CONTEXT_CHIPS)[number];
export type RedLineOpenDuration = (typeof RED_LINE_OPEN_DURATIONS)[number];
export type RedLineClosingPreset = (typeof RED_LINE_CLOSING_PRESETS)[number];

const SESSION_TRANSITIONS: Readonly<Record<RedLineSessionState, readonly RedLineSessionState[]>> = {
  OPENING: ['ACTIVE', 'ENDED'],
  ACTIVE: ['IDLE', 'RECONNECTING', 'ENDED', 'CONVERTED'],
  IDLE: ['ACTIVE', 'ENDED', 'CONVERTED'],
  RECONNECTING: ['ACTIVE', 'ENDED'],
  ENDED: [],
  CONVERTED: [],
};

export function canTransitionRedLineSession(
  from: RedLineSessionState,
  to: RedLineSessionState,
): boolean {
  return SESSION_TRANSITIONS[from].includes(to);
}

export function assertRedLineSessionTransition(
  from: RedLineSessionState,
  to: RedLineSessionState,
): void {
  if (!canTransitionRedLineSession(from, to)) {
    throw new Error(`Invalid Red Line session transition: ${from} -> ${to}`);
  }
}

export function redLineSessionTransitionsFrom(
  state: RedLineSessionState,
): readonly RedLineSessionState[] {
  return SESSION_TRANSITIONS[state];
}

const SIGNAL_EMOJI_MAP: Readonly<Record<RedLineSignalReaction, string>> = {
  PYAH: 'PYAH',
  WAIT: 'WAIT',
  LMAO: 'LMAO',
  REAL: 'REAL',
  LOUDER: 'LOUDER',
  HUH: 'HUH?',
  WARNING: '\u26A0',
  EYE: '\uD83D\uDC41',
};

export function signalReactionLabel(reaction: RedLineSignalReaction): string {
  return SIGNAL_EMOJI_MAP[reaction] ?? reaction;
}

export const RedLineStatusResponseSchema = z.object({
  state: z.enum(RED_LINE_PUBLIC_STATES),
  available: z.boolean(),
  capacity: z.number().int().positive(),
  activeCount: z.number().int().nonnegative(),
  openUntil: z.string().datetime().nullable(),
  adminHeartbeatFresh: z.boolean(),
  retryAfterSeconds: z.number().int().positive().nullable(),
});

export const RedLineOpenRequestSchema = z.object({
  idempotencyKey: z.string().uuid(),
  contextChip: z.enum(RED_LINE_CONTEXT_CHIPS).optional(),
});

export const RedLineOpenResponseSchema = z.object({
  conversationId: z.string().min(8).max(80),
  mode: z.literal('red_line'),
  state: z.enum(RED_LINE_SESSION_STATES),
  capabilityToken: z.string(),
  realtimeTicket: z.string(),
  priorityPingsRemaining: z.number().int().nonnegative(),
  recovered: z.boolean(),
});

export const RedLineOccupiedResponseSchema = z.object({
  code: z.literal('RED_LINE_OCCUPIED'),
  state: z.literal('OCCUPIED'),
  retryAfterSeconds: z.number().int().positive(),
});

export const RedLineSignalRequestSchema = z.object({
  reaction: z.enum(RED_LINE_SIGNAL_REACTIONS),
});

export const RedLineMessageCreateSchema = z.object({
  message: z.string().trim().min(1).max(10_000),
  clientMessageId: z.string().uuid(),
  emphasis: z.enum(RED_LINE_MESSAGE_EMPHASIS).default('NORMAL'),
  contextChip: z.enum(RED_LINE_CONTEXT_CHIPS).optional(),
});

export const RedLineMessageViewSchema = z.object({
  publicId: z.string().min(8).max(80),
  sender: z.enum(['visitor', 'admin', 'system']),
  bodyMarkdown: z.string().max(10_000),
  emphasis: z.enum(RED_LINE_MESSAGE_EMPHASIS),
  contextChip: z.enum(RED_LINE_CONTEXT_CHIPS).nullable(),
  createdAt: z.string().datetime(),
});

export const RedLineMessagesResponseSchema = z.object({
  messages: z.array(RedLineMessageViewSchema),
});

export const RedLinePingResponseSchema = z.object({
  pinged: z.boolean(),
  remaining: z.number().int().nonnegative(),
});

export const RedLineAdminSettingsSchema = z.object({
  openDurationSeconds: z
    .number()
    .int()
    .positive()
    .max(24 * 60 * 60)
    .optional(),
  maxConcurrentSessions: z.number().int().positive().max(10).optional(),
  idleTimeoutSeconds: z
    .number()
    .int()
    .positive()
    .max(60 * 60)
    .optional(),
  maxSessionDurationSeconds: z
    .number()
    .int()
    .positive()
    .max(120 * 60)
    .optional(),
  visitorReopenCooldownSeconds: z
    .number()
    .int()
    .positive()
    .max(24 * 60 * 60)
    .optional(),
});

export const RedLineAdminOpenRequestSchema = z.object({
  durationSeconds: z
    .number()
    .int()
    .refine(
      (value) => (RED_LINE_OPEN_DURATIONS as readonly number[]).includes(value),
      'Choose a supported open duration.',
    ),
});

export const RedLineAdminCloseRequestSchema = z.object({
  preset: z.enum(RED_LINE_CLOSING_PRESETS).optional(),
});

export const RedLineAdminEndRequestSchema = z.object({
  preset: z.enum(RED_LINE_CLOSING_PRESETS).optional(),
});

export const RedLineAdminBlockRequestSchema = z.object({
  reason: z.string().trim().max(500).optional(),
});

export const RedLineAdminStatusResponseSchema = z.object({
  availabilityState: z.enum(RED_LINE_AVAILABILITY_STATES),
  sessionState: z.enum(RED_LINE_SESSION_STATES).nullable(),
  capacity: z.number().int().positive(),
  activeCount: z.number().int().nonnegative(),
  openUntil: z.string().datetime().nullable(),
  adminHeartbeatFresh: z.boolean(),
  lastStateChange: z.string().datetime(),
  activeSession: z
    .object({
      conversationPublicId: z.string(),
      sessionState: z.enum(RED_LINE_SESSION_STATES),
      startedAt: z.string().datetime(),
      lastActivityAt: z.string().datetime(),
      idleWarningSent: z.boolean(),
      priorityPingsUsed: z.number().int().nonnegative(),
      visitorPresent: z.boolean(),
    })
    .nullable(),
  blocked: z.boolean(),
});

export type RedLineStatusResponse = z.infer<typeof RedLineStatusResponseSchema>;
export type RedLineOpenRequest = z.infer<typeof RedLineOpenRequestSchema>;
export type RedLineOpenResponse = z.infer<typeof RedLineOpenResponseSchema>;
export type RedLineOccupiedResponse = z.infer<typeof RedLineOccupiedResponseSchema>;
export type RedLineSignalRequest = z.infer<typeof RedLineSignalRequestSchema>;
export type RedLineMessageCreateInput = z.infer<typeof RedLineMessageCreateSchema>;
export type RedLineMessageView = z.infer<typeof RedLineMessageViewSchema>;
export type RedLineMessagesResponse = z.infer<typeof RedLineMessagesResponseSchema>;
export type RedLinePingResponse = z.infer<typeof RedLinePingResponseSchema>;
export type RedLineAdminSettingsInput = z.infer<typeof RedLineAdminSettingsSchema>;
export type RedLineAdminOpenRequest = z.infer<typeof RedLineAdminOpenRequestSchema>;
export type RedLineAdminCloseRequest = z.infer<typeof RedLineAdminCloseRequestSchema>;
export type RedLineAdminEndRequest = z.infer<typeof RedLineAdminEndRequestSchema>;
export type RedLineAdminBlockRequest = z.infer<typeof RedLineAdminBlockRequestSchema>;
export type RedLineAdminStatusResponse = z.infer<typeof RedLineAdminStatusResponseSchema>;
